import { createContext, useContext, type ReactNode } from 'react'
import { createBrowserShenAiClient } from './browser-shenai-client'
import type { ShenAiVitalClient } from './shenai-types'

/**
 * A FACTORY rather than a client instance, for the reason `MindsetClientProvider` gives: the client
 * is built around a camera the flow opened inside the patient's tap and a canvas that does not exist
 * until the scan screen is rendered, so the seam is the thing that makes a client.
 */
export type ShenAiClientFactory = (options: {
  /** The camera the flow opened inside the patient's tap (docs/decisions.md D-24). */
  readonly stream?: MediaStream | null
  /** The canvas the vendor renders into; its pipeline is WebGL2 on a canvas. */
  readonly canvasId: string
  /**
   * Whether the vendor draws its face mesh. Omitted means yes, which is the single-vendor scan; a
   * hybrid scan passes false because that canvas is underneath an opaque video (docs/decisions.md
   * D-38).
   */
  readonly drawFaceMask?: boolean
  /**
   * Which session to run: the vendor's one-minute all-metrics preset (the default, and the
   * single-vendor scan), or the hybrid scan's thirty seconds of four metrics (docs/decisions.md D-40).
   */
  readonly measurement?: 'oneMinuteAllMetrics' | 'thirtySecondsAllMetrics'
  /**
   * The shape of the frames the camera is delivering, reported once, as width over height.
   *
   * READ OFF THE STREAM THIS CLIENT ALREADY HAS, and it changes nothing about when that stream is
   * opened or who opens it. The scan surface needs it to crop the vendor's own preview to its frame
   * (scanning-screen.tsx) and there is no `<video>` on this path to measure instead.
   */
  readonly onCameraAspect?: (widthOverHeight: number) => void
}) => ShenAiVitalClient

const ShenAiClientContext = createContext<ShenAiClientFactory | null>(null)

/**
 * Production renders with no `factory` prop and gets the real browser adapter; every unit and
 * component test passes a fake, so no test loads the 33 MB vendor bundle, opens a camera or reaches
 * `api.shen.ai`.
 */
export function ShenAiClientProvider({
  factory,
  children,
}: {
  factory?: ShenAiClientFactory
  children: ReactNode
}) {
  return (
    <ShenAiClientContext.Provider value={factory ?? createBrowserShenAiClient}>
      {children}
    </ShenAiClientContext.Provider>
  )
}

export function useShenAiClientFactory(): ShenAiClientFactory {
  const factory = useContext(ShenAiClientContext)
  if (!factory) {
    throw new Error('useShenAiClientFactory() called outside a ShenAiClientProvider')
  }
  return factory
}
